import type React from "react";
import { useEffect, useState } from "react";
import { strings } from "@/I18n";
import { cn } from "@/lib/utils";

interface DisplayValueProps {
  value: unknown;
  className?: string;
  fallback?: string;
  children?: React.ReactNode;
}

/**
 * DisplayValue component for rendering values with consistent N/A fallback.
 * Use this for table cells, details panels, and anywhere missing data should show "N/A".
 *
 * @example
 * // Basic usage - shows "N/A" if value is null/undefined/empty
 * <DisplayValue value={user.email} />
 *
 * @example
 * // With custom content when value exists
 * <DisplayValue value={battery}>
 *   <span className="tw-text-green-500">{battery}%</span>
 * </DisplayValue>
 *
 * @example
 * // With custom fallback (rare - prefer "N/A" for consistency)
 * <DisplayValue value={name} fallback="No name" />
 */
export function DisplayValue({
  value,
  className,
  fallback = strings("common.notApplicable"),
  children,
}: DisplayValueProps) {
  const isEmpty = value == null || value === "";

  if (isEmpty) {
    return (
      <span className={cn("tw-text-muted-foreground tw-italic", className)}>
        {fallback}
      </span>
    );
  }

  return <>{children ?? value}</>;
}

/**
 * Utility function for non-JSX contexts (e.g., useMemo arrays, data transforms).
 * Returns the string "N/A" for null/undefined/empty values.
 *
 * @example
 * const formattedData = data.map(item => ({
 *   ...item,
 *   city: displayValue(item.cityName)
 * }))
 */
export function displayValue(
  value: unknown,
  fallback = strings("common.notApplicable")
): string {
  if (value == null || value === "") {
    return fallback;
  }
  return String(value);
}

interface DurationProps {
  seconds: number | null | undefined;
  className?: string;
}

/**
 * Duration component for consistent duration display across all tables.
 * Shows duration in a readable format: "2h 15m 30s", "15m 30s", or "30s"
 *
 * @example
 * <Duration seconds={3690} /> // renders: 1h 1m 30s
 * <Duration seconds={90} />   // renders: 1m 30s
 * <Duration seconds={45} />   // renders: 45s
 */
export function Duration({ seconds, className }: DurationProps) {
  if (seconds == null || seconds === 0) {
    return (
      <span className={cn("tw-text-muted-foreground tw-italic", className)}>
        {strings("common.notApplicable")}
      </span>
    );
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  return (
    <span className={cn("tw-whitespace-nowrap", className)}>
      {hours > 0 && (
        <>
          {hours}
          <span className="tw-text-muted-foreground">h</span>{" "}
        </>
      )}
      {(hours > 0 || minutes > 0) && (
        <>
          {minutes}
          <span className="tw-text-muted-foreground">m</span>{" "}
        </>
      )}
      {secs}
      <span className="tw-text-muted-foreground">s</span>
    </span>
  );
}

interface DurationMinutesProps {
  minutes: number | null | undefined;
  className?: string;
}

/**
 * DurationMinutes component for displaying duration in minutes.
 * Shows duration in a readable format: "2h 15m", "30m"
 *
 * @example
 * <DurationMinutes minutes={90} />  // renders: 1h 30m
 * <DurationMinutes minutes={45} />  // renders: 45m
 */
export function DurationMinutes({ minutes, className }: DurationMinutesProps) {
  if (minutes == null || minutes === 0) {
    return (
      <span className={cn("tw-text-muted-foreground tw-italic", className)}>
        {strings("common.notApplicable")}
      </span>
    );
  }

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  return (
    <span className={cn("tw-whitespace-nowrap", className)}>
      {hours > 0 && (
        <>
          {hours}
          <span className="tw-text-muted-foreground">h</span>{" "}
        </>
      )}
      {mins}
      <span className="tw-text-muted-foreground">m</span>
    </span>
  );
}

interface LiveDurationProps {
  startTime: string | Date | null | undefined;
  className?: string;
}

/**
 * LiveDuration component for displaying a live-updating duration timer.
 * Calculates duration from startTime to now and updates every second.
 * Use this for active rentals to show real-time elapsed time.
 *
 * @example
 * <LiveDuration startTime="2024-01-15T10:30:00" /> // updates every second
 */
export function LiveDuration({ startTime, className }: LiveDurationProps) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!startTime) return;

    const calculateElapsed = () => {
      const start = new Date(
        startTime +
          (typeof startTime === "string" && !startTime.endsWith("Z") ? "Z" : "")
      );
      const now = new Date();
      return Math.max(0, Math.floor((now.getTime() - start.getTime()) / 1000));
    };

    setElapsed(calculateElapsed());

    const interval = setInterval(() => {
      setElapsed(calculateElapsed());
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime]);

  if (!startTime) {
    return (
      <span className={cn("tw-text-muted-foreground tw-italic", className)}>
        {strings("common.notApplicable")}
      </span>
    );
  }

  const hours = Math.floor(elapsed / 3600);
  const minutes = Math.floor((elapsed % 3600) / 60);
  const secs = elapsed % 60;

  return (
    <span
      className={cn(
        "tw-whitespace-nowrap tw-font-mono tw-tabular-nums",
        className
      )}
    >
      {hours > 0 && (
        <>
          {hours}
          <span className="tw-text-muted-foreground">h</span>{" "}
        </>
      )}
      {(hours > 0 || minutes > 0) && (
        <>
          {minutes}
          <span className="tw-text-muted-foreground">m</span>{" "}
        </>
      )}
      {secs}
      <span className="tw-text-muted-foreground">s</span>
      <span className="tw-ml-1.5 tw-inline-block tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-green-500 tw-animate-pulse" />
    </span>
  );
}
